import React from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Link } from "react-router-dom";

// What a second admin has to look at: adjustments above the approval limit
// and commission clawbacks (counted in pay only once approved), payouts
// added to a month after it was locked, and full & final settlements of
// people who left.

const GET_APPROVALS = gql`
  query GetApprovals {
    getPendingAdjustments {
      status
      message
      adjustments {
        id
        employeeId
        fullName
        type
        subCategoryLabel
        amount
        credit
        description
        date
        createdAt
      }
    }
    getPendingSupplementaryPayouts {
      status
      message
      payouts {
        payoutId
        fullName
        month
        year
        salary
        commission
        leaveDeduction
        adjustmentTotal
        net
        reason
        createdAt
      }
    }
    getPendingSettlements {
      status
      message
      rows {
        employeeId
        fullName
        leavingDate
        status
        net
      }
    }
  }
`;

const DECIDE = gql`
  mutation DecideAdjustment($id: ID!, $approve: Boolean!, $note: String) {
    decideAdjustment(id: $id, approve: $approve, note: $note) {
      status
      message
    }
  }
`;

const DECIDE_SUPPLEMENTARY = gql`
  mutation DecideSupplementaryPayout($payoutId: ID!, $approve: Boolean!) {
    decideSupplementaryPayout(payoutId: $payoutId, approve: $approve) {
      status
      message
    }
  }
`;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const TYPE = {
  CLAWBACK: "Commission clawback",
  BONUS: "Bonus",
  INCENTIVE: "Incentive",
  MANUAL_DEDUCTION: "Deduction",
  ADVANCE_PAYMENT: "Advance",
  TOOLS_CHARGES: "Tools charges",
  TRAINING_CHARGES: "Training charges",
  REFERRAL_BONUS: "Referral bonus",
  SOP_DEDUCTION: "SOP deduction",
  FEEDBACK_PENALTY: "Feedback penalty",
};
const FNF_STATUS = {
  NOT_STARTED: ["Not started", "bg-yellow-100 text-yellow-800"],
  DRAFT: ["Prepared - to settle", "bg-blue-100 text-blue-800"],
  SETTLED: ["Settled", "bg-green-100 text-green-800"],
};

const Approvals = () => {
  const { data, loading, refetch } = useQuery(GET_APPROVALS, { fetchPolicy: "network-only" });
  const [decide] = useMutation(DECIDE);
  const [decideSupplementary] = useMutation(DECIDE_SUPPLEMENTARY);
  const adjustments = data?.getPendingAdjustments?.adjustments || [];
  const added = data?.getPendingSupplementaryPayouts?.payouts || [];
  const settlements = data?.getPendingSettlements?.rows || [];

  const act = async (a, approve) => {
    const note = window.prompt(`${approve ? "Approve" : "Reject"} ${TYPE[a.type] || a.type} of ${inr(a.amount)} for ${a.fullName}? Note (optional):`, "");
    if (note === null) return;
    try {
      const out = (await decide({ variables: { id: a.id, approve, note: note || null } })).data.decideAdjustment;
      if (out.status !== "SUCCESS") alert(out.message);
      refetch();
    } catch (err) {
      alert(err.message);
    }
  };

  const actSupplementary = async (p, approve) => {
    const month = `${MONTHS[p.month - 1]} ${p.year}`;
    if (!window.confirm(approve
      ? `Approve ${inr(p.net)} for ${p.fullName} in ${month} payroll? It can be paid once approved.`
      : `Reject and remove ${p.fullName} from ${month} payroll?`)) return;
    try {
      const out = (await decideSupplementary({ variables: { payoutId: p.payoutId, approve } })).data.decideSupplementaryPayout;
      if (out.status !== "SUCCESS") alert(out.message);
      refetch();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  return (
    <div className="space-y-6">
      <div className="border rounded-lg">
        <div className="px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-800">Adjustments waiting for approval</span>
          <p className="text-xs text-gray-500">
            Not counted in pay until approved. With maker-checker on, the admin who added one can't approve it.
            Clawbacks are raised automatically when a booking is cancelled after its commission was paid.
          </p>
        </div>
        {adjustments.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">Nothing waiting</p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600">
            <tbody className="divide-y">
              {adjustments.map((a) => (
                <tr key={a.id}>
                  <td className="py-2 px-4 text-gray-800 whitespace-nowrap">{a.fullName}</td>
                  <td className="py-2 px-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${a.type === "CLAWBACK" ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-700"}`}>
                      {TYPE[a.type] || a.type}
                    </span>
                    {a.subCategoryLabel && <div className="text-[11px] text-gray-500 mt-0.5">{a.subCategoryLabel}</div>}
                  </td>
                  <td className={`py-2 px-4 font-semibold whitespace-nowrap ${a.credit ? "text-green-700" : "text-red-700"}`}>
                    {a.credit ? "+" : "-"}
                    {inr(a.amount)}
                  </td>
                  <td className="py-2 px-4 text-xs">{a.description}</td>
                  <td className="py-2 px-4 text-xs text-gray-500 whitespace-nowrap">{a.date}</td>
                  <td className="py-2 px-4 whitespace-nowrap space-x-3">
                    <button onClick={() => act(a, true)} className="text-xs font-semibold text-green-700 hover:text-green-900">
                      Approve
                    </button>
                    <button onClick={() => act(a, false)} className="text-xs font-semibold text-red-600 hover:text-red-800">
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border rounded-lg">
        <div className="px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-800">Added to a locked payroll</span>
          <p className="text-xs text-gray-500">
            Professionals left out of a month that was already locked. They can be paid once another admin approves.
          </p>
        </div>
        {added.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">Nothing waiting</p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600">
            <tbody className="divide-y">
              {added.map((p) => (
                <tr key={p.payoutId}>
                  <td className="py-2 px-4 text-gray-800 whitespace-nowrap">{p.fullName}</td>
                  <td className="py-2 px-4 text-xs whitespace-nowrap">{MONTHS[p.month - 1]} {p.year}</td>
                  <td className="py-2 px-4 text-xs text-gray-500 whitespace-nowrap">
                    Salary {inr(p.salary)} · Commission {inr(p.commission)}
                    {p.leaveDeduction > 0 ? ` · Leave -${inr(p.leaveDeduction)}` : ""}
                    {p.adjustmentTotal !== 0 ? ` · Adj ${p.adjustmentTotal > 0 ? "+" : ""}${inr(p.adjustmentTotal)}` : ""}
                  </td>
                  <td className="py-2 px-4 font-semibold whitespace-nowrap">{inr(p.net)}</td>
                  <td className="py-2 px-4 text-xs">"{p.reason}"</td>
                  <td className="py-2 px-4 whitespace-nowrap space-x-3">
                    <button onClick={() => actSupplementary(p, true)} className="text-xs font-semibold text-green-700 hover:text-green-900">
                      Approve
                    </button>
                    <button onClick={() => actSupplementary(p, false)} className="text-xs font-semibold text-red-600 hover:text-red-800">
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border rounded-lg">
        <div className="px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-800">Full &amp; final settlements</span>
          <p className="text-xs text-gray-500">
            Everyone marked as left with a last working day. Prepare it in their dashboard; another admin settles it.
          </p>
        </div>
        {settlements.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">Nobody</p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600">
            <tbody className="divide-y">
              {settlements.map((r) => {
                const [label, cls] = FNF_STATUS[r.status] || [r.status, "bg-gray-100"];
                return (
                  <tr key={r.employeeId}>
                    <td className="py-2 px-4 text-gray-800">{r.fullName}</td>
                    <td className="py-2 px-4 text-xs text-gray-500">Left {r.leavingDate}</td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>{label}</span>
                    </td>
                    <td className="py-2 px-4 text-xs">
                      {r.net === null ? "" : r.net >= 0 ? `Pay ${inr(r.net)}` : `Collect ${inr(-r.net)}`}
                    </td>
                    <td className="py-2 px-4">
                      <Link
                        to={`/admin/employee-dashboard/${r.employeeId}?tab=settlement`}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Approvals;
